"use client";

import { Modal, ModalContent } from "@heroui/react";
import { useDisclosure } from "@heroui/use-disclosure";
import {
  createContext,
  ReactNode,
  startTransition,
  useCallback,
  useContext,
  useRef,
  useState,
  useTransition,
} from "react";
import OTPInput from "react-otp-input";
import Image from "next/image";
import { Button } from "@/components/custom";
// import { VerifySecurityPin } from "@/lib/actions";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { RequestOTP, SubmitOTP } from "@/lib/actions";
import Countdown from "react-countdown";

interface OTPContextType {
  openOTPModal: () => Promise<void>;
  closeOTPModal: (failed?: boolean, reason?: string) => void;
}

const OTPContext = createContext<OTPContextType>({} as OTPContextType);

export const OTPProvider = ({ children }: { readonly children: ReactNode }) => {
  const t = useTranslations();

  const countdownRef = useRef<Countdown>(null);
  const resolveRef = useRef<(value: void | PromiseLike<void>) => void>();
  const rejectRef = useRef<(reason?: any) => void>();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [otp, setOtp] = useState<string>();
  const [otpId, setOtpId] = useState<number>();
  const [date, setDate] = useState(Date.now() + 60 * 1000);
  const [invalid, setInvalid] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);

  const [pending, startTransition] = useTransition();

  const onResend = () => {
    startTransition(async () => {
      await PerformRequestOTP();
      setOtpExpired(false);
      setOtp("");
      countdownRef.current?.start();
    });
  };

  const PerformRequestOTP = async () => {
    const res = await RequestOTP();
    console.log(res);

    if (res.message && res.message.indexOf("verified") !== -1) {
      console.log("OTP Verified");
      if (resolveRef.current) {
        resolveRef.current();
        resolveRef.current = undefined;
      }
      return Promise.reject("otp_verified");
    }

    if (res.message && res.message.indexOf("limit exceeded") !== -1) { 
      console.log("OTP Limit Exceeded");
      return Promise.reject("otp_limit_exceeded");
    }

    if (res.message) {
      console.log("OTP Existed: ", res.message);
      const seconds = parseInt(res.message.replaceAll(/\D/g, ""));
      setOtpId(parseInt(localStorage.getItem("otp_id") as string));
      setDate(Date.now() + seconds * 1000);
      countdownRef.current?.start();
    }
    if (res.id) {
      console.log("New OTP: ", res.id);
      localStorage.setItem("otp_id", res.id.toString());
      setDate(Date.now() + 60 * 1000);
      countdownRef.current?.start();
      setOtpId(res.id);
    }
    return Promise.resolve();
  };

  const openModal: () => Promise<void> = () => {
    startTransition(async () =>
    {
      try {
        await PerformRequestOTP();
        onOpen();
      } catch (error) {
        if (error === "otp_verified") {
          console.log("OTP Verified");
          // closeModal();
        } else if (error === "otp_limit_exceeded") {
          console.log("OTP Limit Exceeded");
          closeModal(true, "OTP Limit Exceeded");
        }
      }
    });
    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
    });
  };

  const closeModal = (failed?: boolean, reason?: string) => {
    if (rejectRef.current && failed) {
      rejectRef.current(reason);
      rejectRef.current = undefined;
    } else if (resolveRef.current) {
      resolveRef.current();
      resolveRef.current = undefined;
    }
    onClose();
  };

  const onSubmit = useCallback(
    () =>
      startTransition(async () => {
        const res = await SubmitOTP(otpId!, parseInt(otp!));
        console.log(res);
        if (res.status_code === 0) {
          console.log("OTP Verified");
          closeModal();
          setOtp("");
          localStorage.removeItem("otp_id");
        } else {
          console.log("OTP Failed");
          setInvalid(true);
        }
      }),
    [otp]
  );

  return (
    <OTPContext.Provider value={{ openOTPModal: openModal, closeOTPModal: closeModal }}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={() => closeModal(true, "Modal closed")}
        size="sm"
        scrollBehavior="inside"
        classNames={{
          body: "bg-[#021517]",
          header: "bg-[#0A2A27] shadow-lg rounded-t-lg",
          closeButton: clsx("hover:bg-white/5 active:bg-white/10"),
        }}
        className="z-[101]"
        placement="center"
        radius="lg"
      >
        <ModalContent className="p-12">
          <div className="flex w-full flex-col items-center gap-4">
            <div className="text-center text-[24px] font-bold">{t("otp-text-header")}</div>
            <div
              className="mt-4 flex flex-col gap-2"
              onFocus={() => {
                setInvalid(false);
              }}
            >
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                inputType="number"
                // renderSeparator={<div className="w-2" />}
                renderInput={(props) => (
                  <input
                    {...props}
                    className="mx-1 aspect-square !h-auto !w-12 rounded-lg border text-center text-lg focus:!outline-yellow-400"
                  />
                )}
              />
              {invalid && <span className="text-center text-tiny text-danger">{t("otp-error-text")}</span>}
            </div>
            <div
              className={clsx("my-4 text-sm", {
                "text-gray-400": pending,
              })}
            >
              {otpExpired ? (
                <>
                  {t.rich("otp-text-b", {
                    resend: (children) => (
                      <span role="button" className="text-primary" onClick={onResend}>
                        {children}
                      </span>
                    ),
                  })}
                </>
              ) : (
                <>
                  {t.rich("otp-text-a", {
                    countdown: () => (
                      <Countdown
                        date={date}
                        onComplete={() => setOtpExpired(true)}
                        renderer={({ minutes, seconds }) =>
                          `${minutes.toLocaleString(undefined, {
                            minimumIntegerDigits: 2,
                          })}:${seconds.toLocaleString(undefined, {
                            minimumIntegerDigits: 2,
                          })}`
                        }
                      />
                    ),
                  })}
                </>
              )}
            </div>
            <div className="my-4 w-full">
              <Button
                onPress={onSubmit}
                className="w-full font-bold text-black disabled:bg-slate-400"
                isDisabled={otp?.length !== 6 || pending}
              >
                {t("submit")}
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </OTPContext.Provider>
  );
};

export const useOTP = () => {
  const context = useContext(OTPContext);
  if (context === undefined) {
    throw new Error("useSecurityPin must be used within a SecurityPinProvider");
  }
  return context;
};
